import usePermissions from '@/hooks/usePermissions';
import { useGetOrganizationsPaymentPlanPendingChangesQuery } from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import { Badge, Box, Button, Card, Icon, Table, useOpenClose } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import {
  OrgV2PaymentPlanChangeStatus,
  NonPlanPaymentEvents,
  PayoutEventMap,
  PendingOrganizationPaymentPlanChanges,
  ProgramType,
  UserPermissions,
} from 'types';

import { useAcceptPaymentPlanAction } from './PendingPlanChangeToAccept.actions';
import { AcceptPendingPlanChangeModal } from './PendingPlanChangeToAccept.modal';

export const FromToComponent = ({ from, to }: { from: string | number; to: string | number }) => {
  return (
    <Box
      direction="row"
      style={{
        whiteSpace: 'nowrap',
      }}
    >
      <Box
        style={{
          display: 'inline',
          textDecoration: 'line-through',
        }}
        color="body-secondary"
      >
        {from}
      </Box>
      <Box
        style={{
          display: 'inline',
          marginLeft: '0.5rem',
        }}
      >
        <Icon name="arrow-right" size={'md'} />
      </Box>
      <Box
        style={{
          display: 'inline',
          marginLeft: '0.5rem',
        }}
        color={'contrast'}
      >
        {to}
      </Box>
    </Box>
  );
};

function mapPendingPlanChangeToTableData(
  pendingPlanChange: PendingOrganizationPaymentPlanChanges,
): Record<string, any> {
  const dataMapping: Record<string, any> = {};
  pendingPlanChange?.currentAndProposedDifferences?.forEach((fromItem) => {
    dataMapping[fromItem.milestone] = {
      id: fromItem.milestone,
      currentPercentage: fromItem.currentPercentage,
      proposedPercentage: fromItem.proposedPercentage,
      currentPausedDays: fromItem.currentPausedDays ?? 0,
      proposedPausedDays: fromItem.proposedPausedDays ?? 0,
    };
  });
  return dataMapping;
}

export const CellComponent = ({ data }: any) => {
  if (!data || !data.currentPercentage || !data.proposedPercentage) return <Box>-</Box>;

  const currentPausedDays = data.currentPausedDays ?? 0;
  const proposedPausedDays = data.proposedPausedDays ?? 0;
  const hasPauseDays = currentPausedDays > 0 || proposedPausedDays > 0;

  return (
    <div>
      <FromToComponent from={data.currentPercentage} to={data.proposedPercentage} />
      {hasPauseDays && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '4px' }}>
          <div style={{ textDecoration: 'line-through', whiteSpace: 'nowrap' }}>
            <Badge message={`${currentPausedDays}d hold`} size="sm" variant="secondary" />
          </div>
          <Icon name="arrow-right" size="md" />
          <div style={{ whiteSpace: 'nowrap' }}>
            <Badge message={`${proposedPausedDays}d hold`} size="sm" variant="primary" />
          </div>
        </div>
      )}
    </div>
  );
};

export const ProposedChangeHeader = ({
  orgPendingPlan,
}: {
  orgPendingPlan?: PendingOrganizationPaymentPlanChanges;
}) => {
  const permissions = usePermissions();
  const proposedPaymentPlanChangeSettingsId = `${orgPendingPlan?.id}`;
  const {
    isOpen: isCancelPaymentProposalModalOpen,
    handleOpen: openCancelPaymentProposalModal,
    handleClose: closeCancelPaymentProposalModal,
  } = useOpenClose();

  const { handleAcceptPaymentPlan, isAccepting } = useAcceptPaymentPlanAction({
    id: proposedPaymentPlanChangeSettingsId,
  });
  const alreadyAccepted = orgPendingPlan?.changeStatus === OrgV2PaymentPlanChangeStatus.accepted;
  const showAcceptProposalButton = permissions?.includes(UserPermissions.lightReachOrganizationSignatory);

  return (
    <Box direction="row" justifyContent="space-between" alignItems="center">
      <Box as="h3" fontWeight="medium" fontSize="md">
        Payment Plan Proposal
        {orgPendingPlan?.effectiveDate
          ? ` - Effective Date: ${DateTime.fromISO(orgPendingPlan?.effectiveDate.toString()).toUTC().toFormat('MM/dd/yyyy')}`
          : ''}
      </Box>
      {orgPendingPlan && !alreadyAccepted && (
        <Box>
          {showAcceptProposalButton ? (
            <Button
              as="a"
              variant={'primary'}
              size={{ base: 'sm', desktop: 'md' }}
              aria-label="Accept Proposal"
              iconPrefix="check"
              onClick={openCancelPaymentProposalModal}
            >
              Accept Proposal
            </Button>
          ) : (
            <Box as="h3" fontWeight="medium" fontSize="md">
              Pending
            </Box>
          )}
        </Box>
      )}
      {orgPendingPlan && alreadyAccepted && (
        <Box as="h3" fontWeight="medium" fontSize="md">
          Accepted
        </Box>
      )}
      <AcceptPendingPlanChangeModal
        isModalOpen={isCancelPaymentProposalModalOpen}
        handleCloseModal={closeCancelPaymentProposalModal}
        dataIsUpdating={isAccepting}
        updateFunction={async () => {
          await handleAcceptPaymentPlan();
          closeCancelPaymentProposalModal();
        }}
        effectiveDate={orgPendingPlan?.effectiveDate}
      />
    </Box>
  );
};

export const PendingPlanChangeToAccept = ({
  organization,
  programType,
}: {
  organization: OrganizationNode;
  programType: ProgramType;
}) => {
  const {
    data: orgPendingPlan,
    isLoading: isLoadingOrgPendingPlan,
    error,
  } = useGetOrganizationsPaymentPlanPendingChangesQuery(
    {
      programType: programType || ProgramType.solar,
      organizationAlias: organization?.alias || '',
    },
    {
      skip: !organization?.alias,
    },
  );
  const isLoading = isLoadingOrgPendingPlan || !organization;
  const tableData = orgPendingPlan && orgPendingPlan[0] ? [mapPendingPlanChangeToTableData(orgPendingPlan[0])] : [];

  const dynamicColumns = useMemo(() => {
    const dynamicColumns = PayoutEventMap[programType]
      .filter((entry) => !NonPlanPaymentEvents.includes(entry.event))
      .toSorted((a, b) => a.order - b.order)
      .map((entry) => ({
        heading: entry?.name,
        dataKey: entry.name,
        render: (_cell?: any) => {
          return <CellComponent data={_cell || undefined} />;
        },
      }));

    dynamicColumns.unshift({
      heading: '',
      dataKey: '',
      render: (_cell?: any) => {
        return (
          <Box
            as="span"
            display="inline"
            style={{
              whiteSpace: 'nowrap',
              //We add negative margin to align the first column with the rest
              //as we cannot directly update the table's cells custom style
              marginLeft: '-24px',
            }}
            color="contrast"
          >
            Payment Percentage
          </Box>
        );
      },
    });

    return dynamicColumns;
  }, [programType]);

  const ErrorComponent = error ? (
    <Box padding="md" textAlign="center">
      There was an error loading the payment schedule proposal (
      {(error as any)?.data?.message || (error as any)?.message}).
    </Box>
  ) : null;

  return (
    <Card padding="0" marginTop="0">
      <Card.Header>
        <ProposedChangeHeader orgPendingPlan={orgPendingPlan && orgPendingPlan[0] ? orgPendingPlan[0] : undefined} />
      </Card.Header>
      <Box margin="md lg">
        {Array.isArray(tableData) && tableData.length === 0 && !isLoadingOrgPendingPlan && !error ? (
          <Box padding="md" textAlign="center">
            No payment schedule proposal found.
          </Box>
        ) : error ? (
          ErrorComponent
        ) : (
          <Table
            columns={dynamicColumns}
            rows={tableData}
            rowKey="heading"
            isLoading={isLoading}
            align="left"
            isScrollable={{
              x: true,
              y: false,
            }}
          />
        )}
      </Box>
    </Card>
  );
};
